import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiefCfemContext } from '../../../contexts/DiefCfemContext';
import { ICrumb } from '../../../interfaces/ICrumb';
import TituloSection from '../../../components/tituloSection/tituloSection';
import CustomDivider from '../../../components/customDivider/customDivider';
import styles from './TitularArrendatarioView.module.scss';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDate';
import { BrButton, BrLoading, BrMessage, BrSelect } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import { IDeclaracaoTitularOuArrendatario } from '../../../interfaces/IDeclaracoes';
import { mesParaAbreviacao } from '../../../utils/mesParaAbreviacao';
import { useBackEndRequests } from '../lib/backEndRequests';
import TitularArrendatarioResumo from '../components/titularArrendatarioResumo/titularArrendatarioResumo';
import { IOperacaoListagemResumo } from '../../../interfaces/IOperacaoPLG';
import TitularArrendatarioIdBasica from '../components/titularArrendatarioIdBasica/titularArrendatarioIdBasica';
import { useNotificacao } from '../../../hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface ISelectOption {
  label: string;
  value: string;
  tipo: string;
}

function TitularArrendatarioView() {
  const urlParams = new URLSearchParams(window.location.search);
  const declaracaoRaiz = urlParams.get('id') ?? '';
  const status = urlParams.get('status') || '';

  const mostrarNotificacao = useNotificacao();

  const [operacaoListagemData, setOperacaoListagemData] = useState<IOperacaoListagemResumo[]>([]);

  const [abaSelecionada, setAbaSelecionada] = useState(1);
  const [isReportOverlayOpen, setIsReportOverlayOpen] = useState(false);

  const [listaDeclaracoes, setListaDeclaracoes] = useState<ISelectOption[]>([]);
  const [declaracaoSelecionada, setDeclaracaoSelecionada] = useState<ISelectOption>();

  const [declaracaoId, setDeclaracaoId] = useState<string>(declaracaoRaiz);

  const {
    getDeclaracaoById,
    getReportRecibo,
    getReportDeclaracaoCompleta,
    getReportDeclaracaoResumida,
    getRetificarDeclaracao,
  } = useBackEndRequests();

  const {
    data: declaracaoData,
    isLoading,
    refetch: refetchDeclaracao,
  } = useQuery<IDeclaracaoTitularOuArrendatario, IErrorWithSentryId>({
    queryKey: ['declaracaoById', declaracaoId],
    queryFn: () => getDeclaracaoById(declaracaoId),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (declaracaoData) {
      const novaListagemData: IOperacaoListagemResumo[] = declaracaoData.aquisicoes || [];

      formatarListaDeclaracoes(declaracaoData.retificadoras);

      setOperacaoListagemData(novaListagemData);
    }
  }, [declaracaoData]);

  const navigate = useNavigate();

  const context = useContext(DiefCfemContext);

  useEffect(() => {
    const crumbs: ICrumb[] = [
      {
        isHome: true,
        label: 'home',
        onClick: () => {
          navigate('/');
        },
      },
      {
        label: 'Titular ou Arrendatário',
        onClick: () => {
          navigate('/titular-ou-arrendatario');
        },
      },
      {
        active: true,
        label: 'Declaração Enviada',
      },
    ];
    context?.setCrumbs(crumbs);
  }, [navigate]);

  const formatarListaDeclaracoes = (listaDeclaracao: any[]) => {
    const numDeclaracoes = listaDeclaracao.length;

    const listaFormatada = listaDeclaracao.map((dec, index) => {
      const posicao = `(${numDeclaracoes - index}/${numDeclaracoes})`;
      const tipo = dec.tipoDeclaracao === 1 ? 'Retificadora' : 'Original';

      return {
        value: dec.id,
        label: `${tipo} - ${dec.dataEmissaoNF} ${posicao}`,
        tipo: `${tipo} ${posicao}`,
      };
    });

    setListaDeclaracoes(listaFormatada);

    const declaracaoExibida = listaFormatada.find((dec) => dec.value === declaracaoId);
    setDeclaracaoSelecionada(declaracaoExibida);
  };

  const prepararReportDownload = (retorno: Blob, tipoArquivo: string) => {
    const reciboDeclaracao = new Blob([retorno], { type: 'application/pdf' });

    const url = URL.createObjectURL(reciboDeclaracao);

    const exercicioMes = mesParaAbreviacao(declaracaoData!.exercicioMes);
    const exercicioAno = declaracaoData!.exercicioAno;
    const numRecibo = declaracaoData!.numeroRecibo;

    const nomeArquivo = `${exercicioMes}_${exercicioAno}_${numRecibo}`;

    const link = document.createElement('a');
    link.href = url;
    link.download = `${tipoArquivo}_${nomeArquivo}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const processarReport = async (fnGetReport: (declaracaoId: string) => Promise<Blob>, tipoArquivo: string) => {
    setIsReportOverlayOpen(true);

    await fnGetReport(declaracaoId)
      .then((retorno) => {
        prepararReportDownload(retorno, tipoArquivo);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível baixar o arquivo solicitado neste momento',
          ...(err && { complementoSentryId: err }),
        });
      })
      .finally(() => {
        setIsReportOverlayOpen(false);
      });
  };

  const baixarRecibo = async () => {
    if (isReportOverlayOpen === false && declaracaoData) {
      processarReport(getReportRecibo, 'recibo');
    }
  };

  const baixarDeclaracaoResumida = async () => {
    if (isReportOverlayOpen === false && declaracaoData) {
      processarReport(getReportDeclaracaoResumida, 'resumo');
    }
  };

  const baixarDeclaracaoCompleta = async () => {
    if (isReportOverlayOpen === false && declaracaoData) {
      processarReport(getReportDeclaracaoCompleta, 'completa');
    }
  };

  const retificarDeclaracao = async () => {
    if (declaracaoRaiz) {
      await getRetificarDeclaracao(declaracaoRaiz)
        .then(async (reficadoraId) => {
          navigate(`/titular-ou-arrendatario/edit?id=${reficadoraId}`);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao retificar a declaração.',
            ...(err && { complementoSentryId: err }),
          });
        });
    } else {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Não foi possível retificar a declaração neste momento',
      });
    }
  };

  const onChangeDeclaracao = async (value: string) => {
    const declaracaoSelecionada = listaDeclaracoes.find((dec) => dec.value === value);
    if (declaracaoSelecionada) {
      setDeclaracaoSelecionada(declaracaoSelecionada);
      setDeclaracaoId(declaracaoSelecionada.value);

      refetchDeclaracao();
    }
  };

  return (
    <>
      {isReportOverlayOpen && (
        <div className={styles.loadingOverlay}>
          <BrLoading large label='Processando Operação...' />
        </div>
      )}

      <CustomDivider
        step={4}
        mes={declaracaoData?.exercicioMes}
        ano={declaracaoData?.exercicioAno}
        hasDadosDeclaracao
      />
      <TituloSection type='titular-ou-arrendatario' isSteps />
      {status === 'enviado' && (
        <BrMessage inlineTitle message='Sua declaração foi enviada!' title='Sucesso.' status='success' closable />
      )}
      {isLoading && (
        <div className={styles.loadContainer}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
      <div>
        <BrMessage
          inlineTitle
          message='De acordo com o art. 8º da Resolução ANM nº 156, de 8 de abril de 2024, a DIEF-CFEM apresentada na forma estabelecida por esta Resolução constitui confissão de dívida e instrumento hábil e suficiente para a exigência dos créditos da CFEM nela consignados (base legal: art. 2º-A, § 5º, da Lei nº 8.001/1990). Emita a(s) guia(s) de recolhimento da CFEM e efetue o(s) pagamento(s) de acordo com os valores declarados.'
          status='info'
          closable
        />
      </div>
      <div className={styles.container}>
        <div>
          <div className={styles.headerContainer}>
            <div className={styles.titleContainer}>
              <span>
                DIEF-CFEM - Entregue - {mesParaAbreviacao(declaracaoData?.exercicioMes || -1)}/
                {declaracaoData?.exercicioAno} <i className={`fas fa-check-circle fa-lg ${styles.greenIcon}`} />
              </span>
              <span className={styles.subtitle}>Titular ou Arrendatário</span>
            </div>

            <div className={styles.selectContainer}>
              <label>
                Declaração
                <BrSelect
                  value={declaracaoSelecionada?.value}
                  onChange={(value: string) => onChangeDeclaracao(value)}
                  options={listaDeclaracoes}
                />
              </label>
            </div>
          </div>

          <div className={styles.statusContainer}>
            <div className={styles.ReciboCard}>
              <i className={`fas fa-receipt ${styles.greenIcon}`} />
              <div className={styles.textRecibo}>
                <span className={styles.statusTitle}>Recibo da declaração</span>
                <span className={styles.statusContent}>{declaracaoData?.numeroRecibo}</span>
              </div>
            </div>
            <div className={styles.statusCard}>
              <span className={styles.statusTitle}>Id da declaração</span>
              <span className={styles.statusContent}>{declaracaoData?.id || ''}</span>
            </div>
            <div className={styles.statusCard}>
              <span className={styles.statusTitle}>Tipo</span>
              <span className={styles.statusContent}>{declaracaoSelecionada?.tipo}</span>
            </div>
            <div className={styles.statusCard}>
              <span className={styles.statusTitle}>Data de entrega</span>
              <span className={styles.statusContent}>{formatDateTime(declaracaoData?.dataRecibo || '')}</span>
            </div>
            <div className={styles.statusCard}>
              <span className={styles.statusTitle}>Valor total de operações</span>
              <span className={styles.statusContent}>{formatCurrency(declaracaoData?.valorTotalOperacoes || '')}</span>
            </div>
          </div>
          <div className={` ${styles.accordion}`}>
            <div className={styles.idbasica}>
              <TitularArrendatarioIdBasica
                declaranteData={declaracaoData?.declarante}
                regimeTributarioData={declaracaoData?.regimeTributacao?.nome}
                isDeclaranteDataLoading={isLoading}
                noBorder
              />
            </div>
            <TitularArrendatarioResumo
              declaracaoSelecionada={declaracaoId}
              isEtapaResumo={false}
              noBorder
              abaSelecionada={abaSelecionada}
              setAbaSelecionada={setAbaSelecionada}
              periodoDeclaracao={
                mesParaAbreviacao(declaracaoData?.exercicioMes || -1) + `/${declaracaoData?.exercicioAno}`
              }
            />
          </div>
        </div>
        <div className={styles.servicosContainer}>
          <span className={styles.servicosTitle}>Serviços Disponíveis</span>
          <div className={styles.servicos}>
            <BrButton onClick={() => baixarRecibo()}>
              <i className={`fas fa-file-download`} /> Baixar Recibo
            </BrButton>

            <BrButton onClick={() => baixarDeclaracaoResumida()}>
              <i className={`fas fa-file-download`} /> Baixar Declaração Resumida
            </BrButton>

            <BrButton onClick={() => baixarDeclaracaoCompleta()}>
              <i className={`fas fa-file-download`} /> Baixar Declaração Completa
            </BrButton>

            <BrButton onClick={() => retificarDeclaracao()}>
              <i className={`fas`}>&#xf2f1;</i> Retificar Declaração
            </BrButton>
          </div>
        </div>
        <div className={styles.actions}>
          <BrButton secondary onClick={() => navigate('/titular-ou-arrendatario')}>
            <i className={`fas fa-times`} /> Fechar
          </BrButton>
        </div>
      </div>
    </>
  );
}

export default TitularArrendatarioView;
